import { ReactNode, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import Factory from '@UI/Widgets/Factory';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { IWidgetDescription, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { isDefined, IsNullOrEmpty, isStringEmpty } from '@Utils/Micro';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { PortKeys, TabsSettings, TabsData, TabsType } from './TabsStructs';
import { yHas } from '@Utils/Utils';

const LayoutTabs = lazy(() => import('./TabsAsync'));
const LayoutTabsSettings = lazy(() => import('./TabsAsync').then(module => ({ default: module.LayoutTabsSettings })));

// =======================================================================================================
// Button Description interface

export class LayoutTabsWidget extends IWidgetThingsClass<TabsSettings> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <LayoutTabs TabsWidgets={this.TabsWidgets} />
        </WidgetsThingsStructs.LazyWidget>

    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <Suspense fallback={<LazyLoadingAnimation />}>
            <LayoutTabsSettings
                key={"Settings-" + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </Suspense>
    }
    // ------------------------------------------------------------------------------------------------
    private CreateTabWidget(x: TabsData) {
        if (isDefined(x.Widget) &&
            !isStringEmpty(x.Widget.Type) &&
            !yHas(this.TabsWidgets, x.Widget.Key)) {
            let widgetDesc = Factory.CreateWidgetThingDesc(x.Widget.Type);
            //widgetDesc = _.merge({}, widgetDesc, x.Widget.Data, { Key: x.Widget.Key });
            widgetDesc = _.defaults({ Key: x.Widget.Key }, x.Widget.Data, widgetDesc);
            // update subwidget thing name with the label 
            widgetDesc.Title = x.Label;
            let widget = Factory.CreateWidgetThing(widgetDesc, widgetDesc.Data, this.WebModule);
            // copy `Description`
            widget.Description = widgetDesc;
            // get `DefaultValues` (if empty)
            if (_.keys(widget.Description.DefaultValues).length == 0)
                widget.Description.DefaultValues = widget.GetDefaultValues();
            this.TabsWidgets[widgetDesc.Key] = widget as IWidgetThingsClass<any>;
        }
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let widgetForDelete: { ThingKey: string, WidgetKey: string }[] = [];
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let webmodule = webNode.GetModule(this.WebModuleKey);

        // Find all widgets that need to be delete
        _.forOwn(this.TabsWidgets, (w: WidgetsThingsStructs.IWidgetThings, wKey) => {
            let f = wData.TabsData.find(t => isDefined(t.Widget) && t.Widget.Key == wKey);
            if (f != undefined) {
                widgetForDelete.push({ ThingKey: w.ThingKey, WidgetKey: f.Widget.Key });
            }
        });

        // Delete them from the webmodule
        _.forEach(widgetForDelete, w => delete this.TabsWidgets[w.WidgetKey]);
        webmodule.RemoveThings(widgetForDelete.map(w => w.ThingKey), true);

        // Init all sub widgets
        _.forEach(wData.TabsData, x => {
            if (isDefined(x.Widget) &&
                !isStringEmpty(x.Widget.Type)) {
                // create a new widget or call update to the already existed
                if (_.has(this.TabsWidgets, x.Widget.Key)) {
                    // Update thing configurations
                    let widgetDesc = Factory.CreateWidgetThingDesc(x.Widget.Type);
                    //this.TabsWidgets[x.Widget.Key].Description = _.merge({}, widgetDesc, x.Widget.Data, { Key: x.Widget.Key });
                    this.TabsWidgets[x.Widget.Key].Description = _.defaults({ Key: x.Widget.Key }, x.Widget.Data, widgetDesc);
                } else
                    this.CreateTabWidget(x);
            }
        });
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Set to clean widgets
        this.TabsWidgets = {};

        let wData = this.WidgetData;

        // Init all sub widgets
        _.forEach(wData.TabsData, x => {
            // Get the default widget desc
            this.CreateTabWidget(x);
        });

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // If there are 2 or more tabs, create additional ports.
        let ports: WebThingPort[] = [];
        if (wData.TabsData.length >= 2) {
            ports = [
                new WebThingPort({
                    Name: 'Open Tab',
                    PortKey: PortKeys.openTab,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                }, WebThingPortType.Data),
                new WebThingPort({
                    Name: 'Hide Tab(s)',
                    PortKey: PortKeys.hideTabs,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                }, WebThingPortType.Data),
                new WebThingPort({
                    Name: 'Unhide Tab(s)',
                    PortKey: PortKeys.unhideTabs,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                }, WebThingPortType.Data),
                new WebThingPort(
                    {
                        Name: 'Settings',
                        PortKey: PortKeys.settings,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Data,
                    data => this.ValidateValues(PortKeys.settings, data)
                ),
                new WebThingPort({
                    Name: 'On Tab Change',
                    PortKey: PortKeys.onTabChange,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: ''
                }, WebThingPortType.Data)
            ];
        }

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;

        // TODO: add the things for the specific widget
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // Validation for incoming event.
        switch (key) {
            case PortKeys.settings:
                return settingsPortValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TabsSettings {
        return {
            TabsData: [],
            Uppercase: true,
            FontSize: 14,
            FontColor: "rgb(255, 255, 255)",
            BackgroundColor: "rgb(34, 45, 50)",
            HighlightColor: "rgb(255, 64, 129)",
            SupportMobileSwipe: true,
            SupportMouseSwipe: false,
            SupportMouseClick: true,
            Type: TabsType.Tabs,
            CarouselSpaceBetween: 0,
            CarouselAutoPlay: false,
            CarouselAutoPlayDelay: 5,
            CarouselAutoPlayDisableOnInteraction: false,
            CarouselShowPagination: true,
            CarouselLoop: true,
            CarouselShowNavigation: false,
            CarouselBulletColor: '#007aff',
            CarouselBulletSize: 8
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return LayoutTabsWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    MigrateDefaultWidgetData(widgetData: TabsSettings): TabsSettings {
        if (!isDefined(widgetData.FontColor))
            widgetData.FontColor = 'rgb(255, 255, 255)';

        if (!isDefined(widgetData.BackgroundColor))
            widgetData.BackgroundColor = 'rgb(34, 45, 50)';

        return widgetData;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: TabsSettings = LayoutTabsWidget.GetDefaultWidgetDataStatic();
        data.TabsData = [
            {
                Icon: "fa fa-cogs",
                Label: "Form with icon",
                Widget: {
                    Key: "FormWidget_123",
                    Type: "FormWidget",
                    Data: null
                }
            },
            {
                Icon: "fa fa-table",
                Widget: {
                    Key: "TableWidget_123",
                    Type: "TableWidget",
                    Data: null
                }
            }
        ];
        return {
            Title: "Tabs",
            WidgetTitle: "",
            Key: "LayoutTabsWidget",
            Type: "LayoutTabsWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            DefiesGrid: false,
            WidgetNoBackground: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/tabs.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;
        _.forEach(this.WidgetData.TabsData, (tabData) => {
            if (!IsNullOrEmpty(tabData.Label) &&
                isDefined(tabData.Widget) &&
                !IsNullOrEmpty(tabData.Widget.Key)) {
                res[tabData.Widget.Key] = tabData.Label;
            }
        });
        return res;
    }
}
// ----------------------------------------------------------------------------------------------------
const settingsPortValidator = () => JsonSchemaValidator.GetInstance(
    'tabs_settings_port_validator',
    {
        type: 'array',
        items: {
            type: 'object',
            required: ['key'],
            properties: {
                key: { type: 'string' },
                label: { type: 'string' },
                icon: { type: 'string' },
                fontColor: { type: 'string' },
                backgroundColor: { type: 'string' }
            }
        }
    }
);
// ----------------------------------------------------------------------------------------------------